import { useEffect, useState, type FormEvent } from 'react';
import { formatDate } from '../../util/datetime';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import * as tenantsApi from '../../api/tenants';
import * as invitationsApi from '../../api/invitations';
import type { TenantAdmin } from '../../api/tenants';
import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import { validateEmail } from '../../validation/profile';
import { RefreshButton } from '../../components/RefreshButton';
import { PendingInvitations } from '../../components/PendingInvitations';
import { SortHeader } from '../../components/SortHeader';
import { useSortedRows } from '../../util/tableSort';

const NO_ADMINS: TenantAdmin[] = [];
const ADMIN_SORT = {
  email: (a: TenantAdmin) => a.email,
  name: (a: TenantAdmin) => a.full_name,
  role: (a: TenantAdmin) => (a.role === 'coordinator' ? 'Coordinator' : 'Admin'),
  verified: (a: TenantAdmin) => (a.email_verified_at ? 'Verified' : 'Pending'),
  enabled: (a: TenantAdmin) => (a.is_active ? 'Active' : 'Suspended'),
  // Coordinators have no read-only variant — they sort with the blanks.
  access: (a: TenantAdmin) =>
    a.role === 'coordinator' ? null : a.read_only ? 'Read-only' : 'Full access',
  joined: (a: TenantAdmin) => a.joined_at,
  lastLogin: (a: TenantAdmin) => a.last_login_at,
};

/**
 * Tenant administrators — peer-managed admin roster for this tenant.
 *
 * Mirrors the superadmin's PlatformAdmins page, but for the
 * tenant_admin role inside a single tenant. Two safety guards live
 * on the backend and surface here as inline errors:
 *
 *   * self-suspend is refused (you can't lock yourself out);
 *   * suspending the *last* active tenant_admin is refused with 409
 *     (the whole reason this page exists is to avoid the lock-out).
 *
 * Invitations reuse the existing POST /tenants/{id}/invitations
 * route with role=tenant_admin. The recipient follows the same
 * accept-invite flow as a participant.
 */
export function TenantAdmins({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const { user: currentUser } = useAuth();
  const tenantId = activeAdminTenantId(currentUser);
  // Read-only admins viewing this page can see the roster but must not
  // be offered invite or toggle controls (the backend 403s anyway).
  const canWrite = canWriteTenant(currentUser, tenantId);
  const [admins, setAdmins] = useState<TenantAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Three-way role picker (TENANT-VIEWER-ROLE): Admin (full write),
  // Coordinator (runs operations, no tenant-shape writes), or Viewer
  // (a read-only admin). Mapped to {role, read_only} at submit.
  type InviteRole = 'admin' | 'coordinator' | 'viewer';
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('admin');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  // Bumped after an invite send so the pending-invitations panel
  // (a separate fetch from the roster) refetches and shows the new row.
  const [pendingReloadKey, setPendingReloadKey] = useState(0);

  const [refreshing, setRefreshing] = useState(false);
  const { sort, sorted } = useSortedRows(admins ?? NO_ADMINS, ADMIN_SORT, 'email');

  const reload = async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      const rows = await tenantsApi.listTenantAdmins(tenantId);
      setAdmins(rows);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to load tenant admins',
      );
      setAdmins([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const toggleActive = async (a: TenantAdmin) => {
    if (!tenantId) return;
    setTogglingId(a.id);
    setError(null);
    // Optimistic flip so the Switch feels responsive. Reverts in the
    // catch if the server rejects (self-toggle 403 or last-admin 409).
    setAdmins((prev) =>
      prev
        ? prev.map((row) =>
            row.id === a.id ? { ...row, is_active: !row.is_active } : row,
          )
        : prev,
    );
    try {
      await tenantsApi.updateTenantAdmin(tenantId, a.id, {
        is_active: !a.is_active,
      });
    } catch (err: unknown) {
      setAdmins((prev) =>
        prev
          ? prev.map((row) =>
              row.id === a.id ? { ...row, is_active: a.is_active } : row,
            )
          : prev,
      );
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to toggle tenant admin',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const toggleReadOnly = async (a: TenantAdmin) => {
    if (!tenantId) return;
    setTogglingId(a.id);
    setError(null);
    // Optimistic flip; reverts on a server reject (self-toggle 403 or
    // last-writable-admin 409).
    setAdmins((prev) =>
      prev
        ? prev.map((row) =>
            row.id === a.id ? { ...row, read_only: !row.read_only } : row,
          )
        : prev,
    );
    try {
      await tenantsApi.updateTenantAdmin(tenantId, a.id, {
        read_only: !a.read_only,
      });
    } catch (err: unknown) {
      setAdmins((prev) =>
        prev
          ? prev.map((row) =>
              row.id === a.id ? { ...row, read_only: a.read_only } : row,
            )
          : prev,
      );
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to change read-only access',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const toggleRole = async (a: TenantAdmin) => {
    if (!tenantId) return;
    const nextRole =
      a.role === 'coordinator' ? 'tenant_admin' : 'coordinator';
    setTogglingId(a.id);
    setError(null);
    // Optimistic swap. Demoting to coordinator drops any read-only flag
    // (coordinators have no read-only variant), mirroring the server.
    setAdmins((prev) =>
      prev
        ? prev.map((row) =>
            row.id === a.id
              ? {
                  ...row,
                  role: nextRole,
                  read_only: nextRole === 'coordinator' ? false : row.read_only,
                }
              : row,
          )
        : prev,
    );
    try {
      await tenantsApi.updateTenantAdmin(tenantId, a.id, { role: nextRole });
    } catch (err: unknown) {
      // Revert on a server reject (self-change 403 or last-admin 409).
      setAdmins((prev) =>
        prev
          ? prev.map((row) =>
              row.id === a.id
                ? { ...row, role: a.role, read_only: a.read_only }
                : row,
            )
          : prev,
      );
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to change role',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const emailError = validateEmail(inviteEmail);
  const inviteDisabled =
    inviteSubmitting ||
    inviteEmail.trim() === '' ||
    emailError !== null;

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setInviteError(null);
    setInviteSentTo(null);
    setInviteSubmitting(true);
    try {
      // Viewer = a read-only tenant_admin; Coordinator = its own role.
      const role = inviteRole === 'coordinator' ? 'coordinator' : 'tenant_admin';
      const read_only = inviteRole === 'viewer';
      await invitationsApi.createInvitation(tenantId, {
        email: inviteEmail.trim(),
        role,
        read_only,
      });
      setInviteSentTo(inviteEmail.trim());
      setPendingReloadKey((k) => k + 1);
      setInviteEmail('');
      setInviteRole('admin');
      // Show success briefly, then close. The roster itself only
      // changes when the invitee accepts; the new invite shows up in
      // the Pending invitations panel (bumped via pendingReloadKey).
      setTimeout(() => {
        setInviteOpen(false);
        setInviteSentTo(null);
      }, 1500);
    } catch (err: unknown) {
      setInviteError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to send invitation',
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  const actions = (
    <Stack direction="row" spacing={1}>
      <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        disabled={!canWrite}
        onClick={() => {
          setInviteEmail('');
          setInviteRole('admin');
          setInviteError(null);
          setInviteSentTo(null);
          setInviteOpen(true);
        }}
      >
        Invite
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={3}>
      {actionsSlot ? (
        createPortal(actions, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Tenant Staff</Typography>
              <Typography variant="body2" color="text.secondary">
                Admins, coordinators and viewers for this tenant. Invite a
                successor before suspending the last active admin — the API
                rejects zero-admin tenants on purpose.
              </Typography>
            </Stack>
          )}
          {actions}
        </Stack>
      )}

      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          everything in this tenant but can't invite admins or change
          access.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {admins === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : admins.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tenant admins yet — this shouldn't happen, since you must
            be one to view this page. Reach out to 10NEWTONS if the
            list is empty.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Email" col="email" sort={sort} />
                <SortHeader label="Name" col="name" sort={sort} />
                <SortHeader label="Role" col="role" sort={sort} />
                <SortHeader label="Verified" col="verified" sort={sort} />
                <SortHeader label="Enabled" col="enabled" sort={sort} />
                <SortHeader label="Access" col="access" sort={sort} />
                <SortHeader label="Joined" col="joined" sort={sort} />
                <SortHeader label="Last login" col="lastLogin" sort={sort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((a) => {
                const isSelf = a.id === currentUser?.id;
                const switchDisabled =
                  isSelf || !canWrite || togglingId === a.id;
                const switchTooltip = !canWrite
                  ? 'Read-only admins can\'t change access.'
                  : isSelf
                  ? "You can't disable your own admin access — ask another tenant admin."
                  : a.is_active
                  ? 'Suspend this admin (their tenant_admin grant becomes inactive)'
                  : 'Re-enable this admin';
                const roTooltip = !canWrite
                  ? 'Read-only admins can\'t change access.'
                  : isSelf
                  ? "You can't change your own access — ask another tenant admin."
                  : a.read_only
                  ? 'Currently read-only — turn on to grant full write access'
                  : 'Currently full access — turn on to make read-only';
                const isAdminRow = a.role !== 'coordinator';
                const roleTooltip = !canWrite
                  ? 'Read-only admins can\'t change roles.'
                  : isSelf
                  ? "You can't change your own role — ask another tenant admin."
                  : isAdminRow
                  ? 'Admin — switch off to make this person a Coordinator'
                  : 'Coordinator — switch on to promote to Admin';
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      {a.email}
                      {isSelf && (
                        <Chip
                          label="you"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{a.full_name ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isAdminRow ? (
                          <Chip
                            label="Admin"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="Coordinator"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        {/* Switch flips the role: checked = Admin,
                            unchecked = Coordinator. Same self / read-only
                            guards as the other toggles; the server also
                            refuses demoting the last active admin. */}
                        <Tooltip title={roleTooltip}>
                          <span>
                            <Switch
                              size="small"
                              checked={isAdminRow}
                              disabled={switchDisabled}
                              onChange={() => void toggleRole(a)}
                              inputProps={{
                                'aria-label': `Toggle role for ${a.email}`,
                              }}
                            />
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {a.email_verified_at ? (
                        <Chip label="Verified" size="small" color="success" />
                      ) : (
                        <Chip label="Pending" size="small" color="warning" />
                      )}
                    </TableCell>
                    {/* Enabled — the active/suspended state and its toggle in
                        one column (was a redundant Status chip + Active
                        switch). Mirrors the Role/Access chip+switch pattern. */}
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {a.is_active ? (
                          <Chip label="Active" size="small" color="success" />
                        ) : (
                          <Chip
                            label="Suspended"
                            size="small"
                            color="warning"
                          />
                        )}
                        <Tooltip title={switchTooltip}>
                          {/* span lets a disabled Switch still anchor the
                              Tooltip — disabled MUI inputs eat pointer
                              events otherwise. */}
                          <span>
                            <Switch
                              size="small"
                              checked={a.is_active}
                              disabled={switchDisabled}
                              onChange={() => void toggleActive(a)}
                              inputProps={{
                                'aria-label': `Toggle active for ${a.email}`,
                              }}
                            />
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {a.role === 'coordinator' ? (
                        // Coordinators have no read-only variant — the
                        // role itself is the access scope.
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          {a.read_only ? (
                            <Chip
                              label="Read-only"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Full access"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                          <Tooltip title={roTooltip}>
                            <span>
                              <Switch
                                size="small"
                                checked={a.read_only}
                                disabled={switchDisabled}
                                onChange={() => void toggleReadOnly(a)}
                                inputProps={{
                                  'aria-label': `Toggle read-only for ${a.email}`,
                                }}
                              />
                            </span>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(a.joined_at)}
                    </TableCell>
                    {/* Never signed in reads "Invited", not a date: this
                        roster had no sign-in signal at all, so an account
                        invited months ago and never opened was
                        indistinguishable from one in daily use. */}
                    <TableCell>
                      {a.last_login_at ? (
                        formatDate(a.last_login_at)
                      ) : (
                        <Chip
                          label="Invited"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invited-but-not-joined admins. Renders nothing when none. */}
      <PendingInvitations
        source={{
          list: () =>
            invitationsApi
              .listInvitations(tenantId)
              .then((r) =>
                r.filter(
                  (i) =>
                    i.role === 'tenant_admin' || i.role === 'coordinator',
                ),
              ),
          resend: (inv) =>
            invitationsApi
              .createInvitation(tenantId, {
                email: inv.email,
                role: inv.role,
                read_only: inv.read_only,
              })
              .then(() => undefined),
          cancel: (inv) => invitationsApi.revokeInvitation(tenantId, inv.id),
        }}
        canWrite={canWrite}
        reloadKey={pendingReloadKey}
      />

      <Dialog
        open={inviteOpen}
        onClose={() => (inviteSubmitting ? null : setInviteOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleInvite}>
          <DialogTitle>Invite to this tenant</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The recipient will receive a magic-link email asking them to
              accept the chosen role for this tenant. If they don't
              already have a 10NEWTONS account, the link lets them set a
              password in one click — no separate signup step.
            </DialogContentText>
            <TextField
              autoFocus
              label="Email"
              type="email"
              fullWidth
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={inviteEmail.trim() !== '' && emailError !== null}
              helperText={
                inviteEmail.trim() !== '' && emailError ? emailError : ' '
              }
              disabled={inviteSubmitting}
            />
            <FormControl sx={{ mt: 1 }} disabled={inviteSubmitting}>
              <FormLabel id="invite-role-label">Role</FormLabel>
              <RadioGroup
                aria-labelledby="invite-role-label"
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as InviteRole)
                }
              >
                <FormControlLabel
                  value="admin"
                  control={<Radio />}
                  label="Admin — full access: settings, staff, billing, and all operations"
                />
                <FormControlLabel
                  value="coordinator"
                  control={<Radio />}
                  label="Coordinator — runs participants, sessions, procedure-instances and devices, but can't change tenant settings, staff or billing"
                />
                <FormControlLabel
                  value="viewer"
                  control={<Radio />}
                  label="Viewer — read-only: sees all data but can't invite, upload, edit or delete"
                />
              </RadioGroup>
            </FormControl>
            {inviteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {inviteError}
              </Alert>
            )}
            {inviteSentTo && !inviteError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Invitation sent to <strong>{inviteSentTo}</strong>. The
                link expires in 7 days.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setInviteOpen(false)}
              disabled={inviteSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviteDisabled}
            >
              {inviteSubmitting ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
}
